// A tiny self-contained WebAudio groove for the café rhythm game. No audio
// files (the game ships as a static bundle) — everything is synthesised from
// oscillators + a noise buffer. It owns the song clock (audio-accurate) so the
// falling notes and the sound stay locked together.
//
// Percussion (kick/snare/hat) and bass are generated on the beat grid; the
// LEAD line is the beatmap itself, so a perfectly-timed tap coincides with the
// melody note you hear. If WebAudio is unavailable/blocked we fall back to a
// performance.now() clock and stay silent — the game still plays.

export class Groove {
  constructor(song){
    this.song = song;
    this.ctx = null;
    this.master = null;
    this.noise = null;
    this.startTime = 0;          // ctx time when song t=0 begins
    this.running = false;
    this._events = [];           // sorted {t, kind, freq}
    this._next = 0;
    this._timer = null;
    this._perfStart = 0;         // fallback clock origin (ms)
  }

  // Build the full audio timeline: percussion + bass on the grid, lead from notes.
  _buildEvents(){
    const s = this.song;
    const ev = [];
    const beats = Math.ceil(s.duration / s.secPerBeat);
    for (let b = 0; b < beats; b++){
      const t = b * s.secPerBeat;
      const inBar = b % 4;                        // 0..3 within a 4/4 bar
      if (inBar === 0 || inBar === 2) ev.push({ t, kind: 'kick' });
      if (inBar === 1 || inBar === 3) ev.push({ t, kind: 'snare' });
      ev.push({ t, kind: 'hat' });               // hat on every beat…
      ev.push({ t: t + s.eighth, kind: 'hat', soft: true }); // …and the offbeat
      if (inBar === 0 || inBar === 2){
        const root = s.bassRoots[Math.floor(b / 4) % s.bassRoots.length];
        ev.push({ t, kind: 'bass', freq: root });
      }
    }
    s.notes.forEach(n => ev.push({ t: n.time, kind: 'lead', freq: s.lanePitches[n.lane] }));
    ev.sort((a, b) => a.t - b.t);
    return ev;
  }

  // Must be called from a user gesture (the "start the set" button) so the
  // AudioContext is allowed to run. Returns true if audio is live.
  async start(){
    this._events = this._buildEvents();
    this._next = 0;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) throw new Error('no WebAudio');
      this.ctx = new AC();
      if (this.ctx.state === 'suspended') await this.ctx.resume();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.32;
      this.master.connect(this.ctx.destination);
      this._buildNoise();
      this.startTime = this.ctx.currentTime + 0.15;   // small lead so nothing clips t=0
      this.running = true;
      this._timer = setInterval(() => this._schedule(), 25);
      return true;
    } catch (e){
      console.warn('café audio unavailable, running silent', e);
      this.ctx = null;
      this._perfStart = performance.now();
      this.running = true;
      return false;
    }
  }

  stop(){
    this.running = false;
    clearInterval(this._timer); this._timer = null;
    if (this.ctx){
      try { this.master.gain.cancelScheduledValues(this.ctx.currentTime); } catch {}
      const c = this.ctx;
      // fade the master out then close so we don't click
      try {
        this.master.gain.setTargetAtTime(0, c.currentTime, 0.05);
        setTimeout(() => c.close().catch(() => {}), 250);
      } catch { c.close().catch(() => {}); }
      this.ctx = null;
    }
  }

  // Song time in seconds since t=0 (audio clock when available).
  now(){
    if (this.ctx) return this.ctx.currentTime - this.startTime;
    return (performance.now() - this._perfStart) / 1000;
  }

  // Fractional beat position, for bobbing the band to the beat.
  beatPhase(){ return this.now() / this.song.secPerBeat; }

  _schedule(){
    if (!this.ctx || !this.running) return;
    const ahead = this.ctx.currentTime + 0.12;      // schedule ~120ms out
    while (this._next < this._events.length){
      const e = this._events[this._next];
      const when = this.startTime + e.t;
      if (when > ahead) break;
      this._voice(e, Math.max(when, this.ctx.currentTime));
      this._next++;
    }
  }

  _buildNoise(){
    const len = Math.floor(this.ctx.sampleRate * 0.4);
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    this.noise = buf;
  }

  _voice(e, when){
    switch (e.kind){
      case 'kick':  return this._kick(when);
      case 'snare': return this._snare(when);
      case 'hat':   return this._hat(when, e.soft ? 0.12 : 0.22);
      case 'bass':  return this._bass(when, e.freq);
      case 'lead':  return this._lead(when, e.freq);
    }
  }

  _env(node, when, peak, attack, release){
    const g = node.gain;
    g.setValueAtTime(0.0001, when);
    g.exponentialRampToValueAtTime(peak, when + attack);
    g.exponentialRampToValueAtTime(0.0001, when + attack + release);
  }

  _kick(when){
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(150, when);
    o.frequency.exponentialRampToValueAtTime(48, when + 0.16);
    this._env(g, when, 0.9, 0.005, 0.17);
    o.connect(g).connect(this.master);
    o.start(when); o.stop(when + 0.24);
  }

  _snare(when){
    const src = this.ctx.createBufferSource(); src.buffer = this.noise;
    const hp = this.ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 1400;
    const g = this.ctx.createGain();
    this._env(g, when, 0.5, 0.005, 0.13);
    src.connect(hp).connect(g).connect(this.master);
    src.start(when); src.stop(when + 0.2);
  }

  _hat(when, dur){
    const src = this.ctx.createBufferSource(); src.buffer = this.noise;
    const hp = this.ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 7000;
    const g = this.ctx.createGain();
    this._env(g, when, dur, 0.002, 0.04);
    src.connect(hp).connect(g).connect(this.master);
    src.start(when); src.stop(when + 0.08);
  }

  _bass(when, freq){
    const o = this.ctx.createOscillator();
    const lp = this.ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 600;
    const g = this.ctx.createGain();
    o.type = 'triangle'; o.frequency.value = freq;
    this._env(g, when, 0.5, 0.01, 0.5);
    o.connect(lp).connect(g).connect(this.master);
    o.start(when); o.stop(when + 0.6);
  }

  _lead(when, freq){
    const o = this.ctx.createOscillator();
    const o2 = this.ctx.createOscillator();     // a touch of detune for warmth
    const lp = this.ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 2600;
    const g = this.ctx.createGain();
    o.type = 'triangle'; o2.type = 'square';
    o.frequency.value = freq; o2.frequency.value = freq * 2; o2.detune.value = 6;
    const g2 = this.ctx.createGain(); g2.gain.value = 0.18;   // quieter square harmonic
    this._env(g, when, 0.32, 0.008, 0.28);
    o.connect(g); o2.connect(g2).connect(g);
    g.connect(lp).connect(this.master);
    o.start(when); o2.start(when);
    o.stop(when + 0.34); o2.stop(when + 0.34);
  }

  // Confirmation blip when the player lands a hit (independent of the song).
  blip(good){
    if (!this.ctx || !this.running) return;
    const when = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = 'sine';
    o.frequency.value = good ? 1320 : 660;
    this._env(g, when, good ? 0.25 : 0.15, 0.003, 0.09);
    o.connect(g).connect(this.master);
    o.start(when); o.stop(when + 0.14);
  }
}
